/**
 * Views — the diagrams BEST can draw, and the ones it cannot.
 *
 * FIRST, the views the MODEL declares (issue #44): SysML `view`s somebody made,
 * each opened on an editable canvas and each creatable here. They are a
 * different kind of thing from everything below - chosen, arranged and saved -
 * which is why they are listed apart.
 *
 * Below them, the generated ones. Four generate from the model with no setting
 * up: the requirements hierarchy and the parts hierarchy, asked for by name, and
 * since issue #94 the TBR and coverage heat maps, counted from the same
 * flowdown rows the requirements hierarchy draws.
 *
 * The rest are listed and SAY PLAINLY that nothing renders them, with the
 * reason. That is not a placeholder for its own sake: an interface block diagram
 * and an engagement sequence are both wanted, both blocked on something specific, and a
 * screen that quietly omitted them would leave somebody wondering whether the
 * feature exists and they cannot find it. Naming the obstacle turns a gap into a
 * work item.
 *
 * Everything positional lives in components/diagram/layout.ts as pure
 * functions, tested apart from anything that draws: a diagram laid out one band
 * too low renders perfectly and is wrong.
 */

import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { ReactElement } from 'react'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import AddIcon from '@mui/icons-material/Add'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '../api'
import { findKind } from '../schema'
import type { EntityRow } from '../schema'
import { useCanWrite, useSchema } from '../state/AppData'
import { MONOSPACE } from '../theme'
import ViewCanvas from '../components/canvas/ViewCanvas'
import PartsHierarchy from '../components/diagram/PartsHierarchy'
import RequirementHeatMap from '../components/diagram/RequirementHeatMap'
import RequirementsHierarchy from '../components/diagram/RequirementsHierarchy'
import CreateEntityDialog from '../components/entity/CreateEntityDialog'
import CreateStateViewDialog from '../components/states/CreateStateViewDialog'
import type { StateViewMode } from '../components/states/CreateStateViewDialog'

/** The part of a view's type a person reads: 'StateTransitionView', not the package path. */
/** A view's name, falling back to its short ID when it declares none. */
function viewTitle(row: EntityRow): string {
  return String(row.name || row.id)
}

function viewKindLabel(typeName: unknown, labels: Record<string, string>): string {
  const text = String(typeName ?? '')
  return labels[text] ?? (text.split('::').pop() || 'Untyped view')
}

interface SavedView {
  key: string
  title: string
  description: string
  /** Null for a view nothing renders. Then `blocked` says what is missing. */
  render: (() => ReactElement) | null
  /**
   * Why nothing draws this yet, in terms of the thing that is actually missing.
   *
   * Specific on purpose. "Coming soon" is unactionable; "the interfaces are
   * stored and nothing draws them" is a sentence somebody can turn into a
   * canvas.
   */
  blocked: string
}

const VIEWS: SavedView[] = [
  {
    key: 'requirements-hierarchy',
    title: 'Requirements hierarchy',
    description:
      'Every requirement banded by level, L0 at the top, tracing up to each source it derives ' +
      'from. Orphans are drawn on their own and marked. Drag a handle onto another box to add ' +
      'a source to a requirement.',
    render: () => <RequirementsHierarchy />,
    blocked: '',
  },
  {
    key: 'parts-hierarchy',
    title: 'Parts hierarchy',
    description:
      'The system decomposition banded by containment depth, following usages through their ' +
      'definitions. Read-only: a part’s place in the tree is where its declaration sits, not a ' +
      'field that can be written.',
    render: () => <PartsHierarchy />,
    blocked: '',
  },
  {
    key: 'tbr-heat-map',
    title: 'TBR heat map',
    description:
      'Where the unresolved requirements are concentrated, subsystem or segment against level, ' +
      'so the next sweep can be aimed.',
    render: () => <RequirementHeatMap measure="unresolved" />,
    blocked: '',
  },
  {
    key: 'coverage-heat-map',
    title: 'Coverage heat map',
    description:
      'The share of requirements a verification case verifies, subsystem or segment against ' +
      'level, so an untested corner of the program shows as a dark cell.',
    render: () => <RequirementHeatMap measure="coverage" />,
    blocked: '',
  },
  {
    key: 'interface-block-diagram',
    title: 'Interface block diagram',
    description:
      'Parts as blocks, with each port drawn on the edge of the part that owns it and a ' +
      'connector along every interface.',
    render: null,
    // The reason this card gave until issue #52 - on_part missing from the port
    // listing - was false: the listing had carried it for some time. What was
    // really missing was the interfaces themselves, which were never stored.
    // They are now, so what is left is only the drawing.
    blocked:
      'Nothing renders this yet. The model side is there — ports on parts, and the ' +
      'interfaces joining them with their ends resolved (GET /api/parts/{id}/connections) — ' +
      'but no canvas yet lays parts out as blocks with their ports on the edge.',
  },
  {
    key: 'engagement-sequence',
    title: 'Engagement sequence',
    description: 'Who does what, in order, across the segments — a sequence over the mission.',
    render: null,
    blocked:
      'Nothing renders this yet. Functions, their steps and the successions ordering them ' +
      'are in the model, and one function’s flow is drawn on an action-flow view; no view ' +
      'yet lays several functions out across the segments on one timeline.',
  },
  {
    key: 'verification-coverage',
    title: 'Verification coverage',
    description:
      'The verification tree with each requirement it covers, so an untested branch of the ' +
      'design is visible as a gap rather than as an absence.',
    render: null,
    blocked:
      'Nothing renders this yet as a diagram. The requirement-by-method matrix lives on the ' +
      'verification traceability screen; a drawn view of it is not built.',
  },
]

/** How many of the generated views draw - counted, so the sentence cannot go stale. */
const DRAWN = VIEWS.filter((view) => view.render).length

export default function Views() {
  // The requirements hierarchy is what somebody opening this screen almost
  // always wants, and it costs one request. Showing it rather than making them
  // choose first is the difference between a screen and a menu.
  // `?view=<id>` opens that model view on arrival - how the States screen's
  // machine overview opens a machine's paired diagram (decision 030).
  const [params] = useSearchParams()
  const [open, setOpen] = useState<string>(() => {
    const asked = params.get('view')
    return asked ? `model:${asked}` : 'requirements-hierarchy'
  })
  const selected = VIEWS.find((view) => view.key === open && view.render)
  // A model view is opened by its identifier, prefixed so it cannot collide
  // with a generated view's key.
  const openModelView = open.startsWith('model:') ? open.slice('model:'.length) : ''

  return (
    <Stack spacing={2}>
      <ModelViews open={openModelView} onOpen={(identifier) => setOpen(`model:${identifier}`)} />

      <Box>
        <Typography variant="h6">Generated</Typography>
        <Typography variant="body2" color="text.secondary">
          Diagrams generated from the model as it is now — there is nothing to save and nothing to
          keep up to date. {DRAWN} of the {VIEWS.length} draw today; the rest say what is missing.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
        }}
      >
        {VIEWS.map((view) => (
          <Card key={view.key} variant="outlined" sx={{ display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                  {view.title}
                </Typography>
                <Chip
                  size="small"
                  label={view.render ? 'Generated' : 'Not drawn'}
                  color={view.render ? 'success' : 'default'}
                  variant={view.render ? 'filled' : 'outlined'}
                />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {view.description}
              </Typography>
              {!view.render && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  {view.blocked}
                </Alert>
              )}
            </CardContent>
            {view.render && (
              <CardActions>
                <Button
                  size="small"
                  startIcon={<AccountTreeIcon />}
                  variant={open === view.key ? 'contained' : 'text'}
                  aria-label={`${open === view.key ? 'Showing' : 'Show'} ${view.title}`}
                  onClick={() => setOpen(view.key)}
                >
                  {open === view.key ? 'Showing' : 'Show'}
                </Button>
              </CardActions>
            )}
          </Card>
        ))}
      </Box>

      {selected && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            {selected.title}
          </Typography>
          {selected.render?.()}
        </Paper>
      )}
    </Stack>
  )
}

/**
 * The diagrams the model itself declares: SysML views, each editable.
 *
 * Listed apart from the generated ones above because they are a different kind
 * of thing - somebody drew these, chose what is on them and where it sits, and
 * that choice is saved in the model and beside it.
 */
function ModelViews({ open, onOpen }: { open: string; onOpen: (identifier: string) => void }) {
  const schema = useSchema()
  const { allowed, reason } = useCanWrite()
  const kind = findKind(schema, 'view')
  const [rows, setRows] = useState<EntityRow[] | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  const [creating, setCreating] = useState(false)
  // A state diagram is paired with one machine (decision 030), made from an
  // existing machine or with a new one; any other kind is the generic form.
  const [menu, setMenu] = useState<HTMLElement | null>(null)
  const [stateView, setStateView] = useState<StateViewMode | null>(null)
  const [reloads, setReloads] = useState(0)
  const reload = useCallback(() => setReloads((count) => count + 1), [])

  useEffect(() => {
    api
      .list(kind?.plural ?? 'views', { limit: 500 })
      .then((page) => setRows(page.items))
      .catch((failure: ApiError) => setError(failure))
  }, [kind?.plural, reloads])

  const labels = Object.fromEntries((kind?.types ?? []).map((choice) => [choice.name, choice.label]))
  // Opened by identifier; a view just created is not listed until the reload
  // lands, so it opens under its identifier alone until then.
  const openRow = open ? (rows ?? []).find((row) => row.id === open) ?? ({ id: open } as EntityRow) : null

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5">Views</Typography>
          <Typography variant="body2" color="text.secondary">
            Diagrams declared in the model. What each shows is what it exposes; where each box sits
            is kept in a layout file beside the model, so arranging one is not a model change.
          </Typography>
        </Box>
        {kind && (
          <Tooltip title={allowed ? 'Declare a new view in the model' : reason}>
            <span>
              <Button startIcon={<AddIcon />} onClick={(event) => setMenu(event.currentTarget)} disabled={!allowed}>
                New view
              </Button>
            </span>
          </Tooltip>
        )}
        <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
          <MenuItem onClick={() => { setMenu(null); setStateView('existing') }}>
            State diagram of an existing machine…
          </MenuItem>
          <MenuItem onClick={() => { setMenu(null); setStateView('new') }}>
            State diagram with a new machine…
          </MenuItem>
          <MenuItem onClick={() => { setMenu(null); setCreating(true) }}>Another kind of view…</MenuItem>
        </Menu>
      </Stack>

      {error && <Alert severity="error">{error.message}</Alert>}
      {rows && rows.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          The model declares no views yet.
        </Typography>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
        }}
      >
        {(rows ?? []).map((row) => (
          <Card key={row.id} variant="outlined" sx={{ display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start', mb: 1 }}>
                {/* The NAME leads - it says what the diagram is; the short ID
                    beneath is what the API and a reference use. */}
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1">{viewTitle(row)}</Typography>
                  {row.name && row.name !== row.id && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONOSPACE }}>
                      {row.id}
                    </Typography>
                  )}
                </Box>
                <Chip size="small" label={viewKindLabel(row.type_name, labels)} />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {String(row.statement || '')}
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                size="small"
                startIcon={<AccountTreeIcon />}
                variant={open === row.id ? 'contained' : 'text'}
                onClick={() => onOpen(row.id)}
              >
                {open === row.id ? 'Showing' : 'Open'}
              </Button>
            </CardActions>
          </Card>
        ))}
      </Box>

      {openRow && (
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', mb: 1 }}>
            <Typography variant="h6">{viewTitle(openRow)}</Typography>
            {openRow.name && openRow.name !== openRow.id && (
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONOSPACE }}>
                {openRow.id}
              </Typography>
            )}
          </Stack>
          <ViewCanvas key={openRow.id} identifier={openRow.id} />
        </Paper>
      )}

      {stateView && (
        <CreateStateViewDialog
          mode={stateView}
          onClose={() => setStateView(null)}
          onCreated={(identifier) => {
            setStateView(null)
            reload()
            onOpen(identifier)
          }}
        />
      )}

      {creating && kind && (
        <CreateEntityDialog
          kind={kind}
          onClose={() => setCreating(false)}
          onCreated={(identifier) => {
            setCreating(false)
            reload()
            onOpen(identifier)
          }}
        />
      )}
    </Stack>
  )
}
